import React, { useState, useEffect, useCallback } from "react";
import { Cell } from "@components/table/types";
import DeleteIcon from "@material-ui/icons/Delete";
import { Manager } from "@components/form/state/types";
import { Form, Modal } from "@components";
import api from "@api";
import { FilterType, Table } from "@components";
import { Field } from "../../../../../../components/form/fields/field";
import { Box, Button } from "@material-ui/core";
import {
  ApproverDistroIdWithMember,
  ApproverDistroMember,
} from "@api/types/approverDistro";
import { alertService } from "@services";
import { ConfirmationModal } from "../../../../../request/steps/summary/views/summary/confirmation-modal";

interface Prop {
  open: boolean;
  selectedApproverDistro: any;
  onCancel: () => void;
}

const headers: Cell[] = [{ data: "Member" }, { data: "Delete" }];

type Key = keyof ApproverDistroIdWithMember;

export const Member: React.FC<Prop> = ({
  open,
  selectedApproverDistro,
  onCancel,
}) => {
  useEffect(() => {
    if (open) {
      getMemberByApproverDistroId();
    } else {
      setMembers([]);
    }
  }, [open]);

  const [members, setMembers] = useState<ApproverDistroMember[]>([]);

  const [isSubmitting, setSubmitting] = useState(false);
  const [isFetchingMembers, setIsFetchingMembers] = useState(false);

  const form = Form.useManager<ApproverDistroIdWithMember>({
    email: "",
    approverDistroId: 0,
  });

  const approverDistroMember: ApproverDistroIdWithMember = {
    email: "",
    approverDistroId: 0,
  };
  const [isConfirming, setConfirmingState] = useState<boolean>(false);
  const [idToBeDeleted, setIdToBeDeleted] = useState<number|null>(null);
  const [modalMessage, setModalMessage] = useState<string>("");

  function closeAndClearData(close: boolean) {
    approverDistroMember.approverDistroId = 0;
    approverDistroMember.email = "";
    form.reset();
    if (close) {
      onCancel();
      selectedApproverDistro = { id: 0, name: "" };
      setMembers([]);
    }
  }

  function getMemberByApproverDistroId() {
    const approverDistroId = selectedApproverDistro.id;
    setIsFetchingMembers(true);
    api.approverDistro
      .getMemberByApproverId(approverDistroId)
      .then((response) => {
        console.log(response);
        setMembers(response);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsFetchingMembers(false);
      });
  }
  
  function handleConfirmationCancel() {
    closeConfirmationModal()
  }

  function handleConfirmationConfirm() {
    setConfirmingState(true);
    deleteMemberById(idToBeDeleted);
  }

  function openConfirmationModal(id: number)
  {
    setModalMessage("Are you sure do you want to remove this data?");
    setIdToBeDeleted(id);
    setConfirmingState(true);
  }

  function closeConfirmationModal()
  {
    setModalMessage("");
    setIdToBeDeleted(null);
    setConfirmingState(false);
  }

  const deleteMemberById = (id: number|null) => {
    if (id) {
      api.approverDistro
        .deleteApproverDistroMemberById(id)
        .then((response) => {
          if (response) {
            alertService.success({
              msg: "Member email has been successfully removed.",
            });
            getMemberByApproverDistroId();
          } else {
            alertService.danger({ msg: "Failed to delete." });
          }
          closeConfirmationModal()
        })
        .catch((error) => {
          console.log(error);
          closeConfirmationModal()
        });
    }
  };

  function submitData() {
    if (selectedApproverDistro.id) {
      setSubmitting(true);
      approverDistroMember.approverDistroId = selectedApproverDistro.id;
      approverDistroMember.email = form.values.email;
      // CREATE
      api.approverDistro
        .postApproverDistroMember(approverDistroMember)
        .then((response) => {
          if (response) {
            alertService.success({
              msg: "Email has been successfully added to this approver distro.",
            });
            getMemberByApproverDistroId();
            closeAndClearData(false);
          } else {
            alertService.danger({ msg: "Failed to save member." });
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  }

  function onChange(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "email":
        return Promise.resolve([
          name as Key,
          Form.Validators.hasValue(value) ||
            Form.Validators.isEmail(value as string),
        ]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  const deleteIcon = (
    <span
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <DeleteIcon />
    </span>
  );

  const handleSubmit = useCallback(() => {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          submitData();
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }, [form]);

  const modalTitle =
    selectedApproverDistro.approverDistroId !== 0
      ? selectedApproverDistro.name
      : "None selected";
  const submitText = isSubmitting ? "Adding..." : "Add";

  return (
    <Modal
      isOpen={open}
      title={`Selected Approver Distro: ${modalTitle}`}
      closeModal={() => closeAndClearData(true)}
      size="lg"
    >
      <Form>
        <Form.Textbox
          label="Email Address"
          name="email"
          onChange={onChange}
          size={6}
          error={form.errors.email}
          value={form.values.email}
          placeholder="Enter Email Address"
          required
        />
      </Form>
      <Form>
        <Field label="" size={1}>
          <Button
            type="button"
            color="primary"
            disabled={isSubmitting}
            onClick={handleSubmit}
          >
            {submitText}
          </Button>
        </Field>
        <Field label="Members" size={12}>
          <Table
            data={members.map((data: any) => ({
              cells: [
                {
                  data: data.email,
                  color: "blue",
                  cursor: "pointer",
                  onClick: () => {},
                },
                {
                  data: deleteIcon,
                  color: "gray",
                  cursor: "pointer",
                  onClick: () => {
                    openConfirmationModal(data.id);
                  },
                },
              ],
              data: data,
            }))}
            tableName={"Members"}
            headers={headers}
            filters={[FilterType.SORT, FilterType.SEARCH, FilterType.PAGINATOR]}
            loading={isFetchingMembers}
          />
        </Field>
      </Form>
      <Box textAlign="right" mt={4}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => closeAndClearData(true)}
        >
          Close
        </Button>
      </Box>
      <ConfirmationModal
        onCancel={handleConfirmationCancel}
        onConfirm={handleConfirmationConfirm}
        open={isConfirming}
        customMessage={modalMessage}
      />
    </Modal>
  );
};
